import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FeedbackSection() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ average: null, count: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/feedback?limit=12`),
      axios.get(`${API}/feedback/stats`),
    ])
      .then(([r1, r2]) => {
        setItems(r1.data);
        setStats(r2.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (rating < 1 || rating > 10) {
      toast.error("Seleziona un voto da 1 a 10");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback`, {
        name: name.trim(),
        rating,
        comment: comment.trim() || null,
      });
      toast.success("Grazie per il tuo feedback!");
      setName(""); setRating(0); setComment(""); setShowForm(false);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg).join(", ") : (detail || "Errore invio feedback");
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="feedback" className="border-t border-slate-800 bg-slate-900/50" data-testid="feedback-section">
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="mb-10 flex items-end justify-between flex-wrap gap-6">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-orange-500 mb-3" data-testid="feedback-tag">
              [ 04 / Feedback dei clienti ]
            </div>
            <h2 className="font-heading text-3xl md:text-5xl text-white tracking-tighter" data-testid="feedback-title">
              La voce di chi ci usa.
            </h2>
            {stats.count > 0 && (
              <p className="text-slate-400 mt-3" data-testid="feedback-stats">
                <span className="font-mono text-orange-500 text-2xl font-bold">{stats.average}</span>
                <span className="text-slate-500 text-sm mx-2">/ 10</span>
                <span className="text-slate-500 text-sm">
                  · {stats.count} recension{stats.count === 1 ? "e" : "i"}
                </span>
              </p>
            )}
          </div>
          <button
            onClick={() => setShowForm((s) => !s)}
            className={showForm ? "tm-btn-outline" : "tm-btn-primary"}
            data-testid="feedback-toggle-btn"
          >
            {showForm ? "Chiudi" : "+ Lascia un feedback"}
          </button>
        </div>

        {showForm && (
          <form onSubmit={submit} className="tm-card p-6 mb-8" data-testid="feedback-form">
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="tm-label">Nome o Nickname *</label>
                <input
                  type="text"
                  required
                  maxLength={60}
                  className="tm-input"
                  placeholder="es. Marco R. / Officina Rossi"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="feedback-name"
                />
              </div>
              <div>
                <label className="tm-label">Voto (1-10) *</label>
                <div className="flex gap-1 flex-wrap" data-testid="feedback-rating-picker">
                  {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setRating(n)}
                      className={`w-9 h-9 border font-mono text-sm transition-colors ${
                        rating >= n ? "border-orange-500 bg-orange-500 text-white" : "border-slate-600 text-slate-400 hover:border-orange-400"
                      }`}
                      data-testid={`feedback-rating-${n}`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="mb-4">
              <label className="tm-label">Commento (opzionale, max 500 caratteri)</label>
              <textarea
                className="tm-textarea"
                maxLength={500}
                placeholder="Cosa ti è piaciuto? Cosa possiamo migliorare?"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                data-testid="feedback-comment"
              />
            </div>
            <button
              type="submit"
              disabled={submitting || rating === 0 || !name.trim()}
              className="tm-btn-primary disabled:opacity-50"
              data-testid="feedback-submit"
            >
              {submitting ? "Invio..." : "Pubblica feedback"}
            </button>
          </form>
        )}

        {loading && (
          <div className="tm-card p-8 text-center font-mono text-slate-500 uppercase tracking-widest text-sm">
            Caricamento...
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="tm-card p-8 text-center" data-testid="feedback-empty">
            <p className="text-slate-400">Nessun feedback ancora. Sii il primo!</p>
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((fb) => (
              <div key={fb.id} className="tm-card p-5" data-testid={`feedback-item-${fb.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-heading text-lg text-white tracking-tight">{fb.name}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-1">
                      {new Date(fb.created_at).toLocaleDateString("it-IT", {
                        day: "2-digit", month: "short", year: "numeric",
                      })}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-2xl font-bold text-orange-500" data-testid={`feedback-rating-value-${fb.id}`}>
                      {fb.rating}<span className="text-slate-500 text-sm">/10</span>
                    </div>
                  </div>
                </div>
                {fb.comment && (
                  <p className="text-slate-300 text-sm leading-relaxed">&ldquo;{fb.comment}&rdquo;</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
